import React, { useState, useEffect } from 'react';
import { Wrench, Clock, CheckCircle, AlertCircle, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Tickets = () => {
  const { t } = useTranslation();
  const [assets, setAssets] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const storedAssets = localStorage.getItem('e2-assets');
    if (storedAssets) {
      const parsedAssets = JSON.parse(storedAssets);
      setAssets(parsedAssets);
      
      // Extract all tickets
      const allTickets = [];
      parsedAssets.forEach(asset => {
        if (asset.maintenanceTickets && asset.maintenanceTickets.length > 0) {
          asset.maintenanceTickets.forEach(ticket => {
            allTickets.push({
              ...ticket,
              assetId: asset.id,
              assetName: asset.name,
              assetType: asset.type,
              location: `Floor ${asset.floor} - ${asset.room}`
            });
          });
        }
      });
      setTickets(allTickets);
    }
  }, []);

  const handleStatusChange = (ticketId, assetId, newStatus) => {
    const updatedAssets = assets.map(asset => {
      if (asset.id === assetId) {
        return {
          ...asset,
          maintenanceTickets: asset.maintenanceTickets.map(ticket => 
            ticket.id === ticketId ? { ...ticket, status: newStatus } : ticket
          ),
          // If ticket is resolved, set asset to ready
          status: newStatus === 'resolved' ? 'ready' : asset.status
        };
      }
      return asset;
    });
    
    localStorage.setItem('e2-assets', JSON.stringify(updatedAssets));
    setAssets(updatedAssets);
    
    // Update local tickets state
    setTickets(tickets.map(t => 
      t.id === ticketId ? { ...t, status: newStatus } : t
    ));
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.assetName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         ticket.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in-progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return '#F45053';
      case 'in-progress': return '#F59E0B';
      case 'resolved': return '#10B981';
      default: return '#6B7280';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'open': return t('tickets.open');
      case 'in-progress': return t('tickets.inProgress');
      case 'resolved': return t('tickets.resolved');
      default: return status;
    }
  };

  return (
    <div className="tickets-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('tickets.title')}</h1>
          <p className="page-subtitle">{t('tickets.subtitle')}</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="tickets-stats">
        <Card className="ticket-stat-card">
          <CardContent className="stat-content">
            <Wrench size={24} className="stat-icon" />
            <div>
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">{t('tickets.total')}</div>
            </div>
          </CardContent>
        </Card>

        <Card className="ticket-stat-card">
          <CardContent className="stat-content">
            <AlertCircle size={24} className="stat-icon" style={{ color: '#F45053' }} />
            <div>
              <div className="stat-value" style={{ color: '#F45053' }}>{stats.open}</div>
              <div className="stat-label">{t('tickets.open')}</div>
            </div>
          </CardContent>
        </Card>

        <Card className="ticket-stat-card">
          <CardContent className="stat-content">
            <Clock size={24} className="stat-icon" style={{ color: '#F59E0B' }} />
            <div>
              <div className="stat-value" style={{ color: '#F59E0B' }}>{stats.inProgress}</div>
              <div className="stat-label">{t('tickets.inProgress')}</div>
            </div>
          </CardContent>
        </Card>

        <Card className="ticket-stat-card">
          <CardContent className="stat-content">
            <CheckCircle size={24} className="stat-icon" style={{ color: '#10B981' }} />
            <div>
              <div className="stat-value" style={{ color: '#10B981' }}>{stats.resolved}</div>
              <div className="stat-label">{t('tickets.resolved')}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="tickets-filters">
        <div className="search-box-large">
          <Search size={20} className="search-icon" />
          <Input
            type="text"
            placeholder={t('tickets.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input-large"
          />
        </div>

        <div className="filter-buttons">
          <Button 
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('all')}
            className="filter-btn"
          >
            {t('tickets.all')}
          </Button>
          <Button 
            variant={statusFilter === 'open' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('open')}
            className="filter-btn"
          >
            {t('tickets.open')}
          </Button>
          <Button 
            variant={statusFilter === 'in-progress' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('in-progress')}
            className="filter-btn"
          >
            {t('tickets.inProgress')}
          </Button>
          <Button 
            variant={statusFilter === 'resolved' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('resolved')}
            className="filter-btn"
          >
            {t('tickets.resolved')}
          </Button>
        </div>
      </div>

      {/* Tickets List */}
      {filteredTickets.length > 0 ? (
        <div className="tickets-list">
          {filteredTickets.map(ticket => (
            <Card key={ticket.id} className="ticket-item-card">
              <CardHeader>
                <div className="ticket-item-header">
                  <div>
                    <CardTitle className="ticket-item-title">{ticket.assetName}</CardTitle>
                    <p className="ticket-item-location">{ticket.location}</p>
                  </div>
                  <Badge 
                    style={{ backgroundColor: getStatusColor(ticket.status) }}
                    className="ticket-status-badge"
                  >
                    {getStatusLabel(ticket.status)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="ticket-item-content">
                  <p className="ticket-description">{ticket.description}</p>
                  <div className="ticket-meta">
                    <span className="ticket-date">
                      {t('tickets.created')}: {new Date(ticket.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  
                  {ticket.status !== 'resolved' && (
                    <div className="ticket-actions">
                      {ticket.status === 'open' && (
                        <Button
                          size="sm"
                          onClick={() => handleStatusChange(ticket.id, ticket.assetId, 'in-progress')}
                        >
                          {t('tickets.startWork')}
                        </Button>
                      )}
                      {ticket.status === 'in-progress' && (
                        <Button
                          size="sm"
                          onClick={() => handleStatusChange(ticket.id, ticket.assetId, 'resolved')}
                        >
                          {t('tickets.markResolved')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="empty-tickets">
          <CardContent className="empty-tickets-content">
            <Wrench size={64} className="empty-icon" />
            <h3 className="empty-title">{t('tickets.noTickets')}</h3>
            <p className="empty-text">
              {searchQuery || statusFilter !== 'all' 
                ? t('assets.adjustFilters')
                : t('tickets.allGood')}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Tickets;
